import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

type MobileTab = 'light' | 'premium';

const comparisonRows = [
  { label: '月額', light: '¥9,900', premium: '¥16,900', highlight: true },
  { label: '基本人数', light: '1名', premium: '2名（本人＋同伴1名）', highlight: false },
  { label: '基本時間', light: '55分', premium: '1時間55分', highlight: false },
  { label: '延長', light: '+1時間まで可', premium: '+2時間まで可', highlight: false },
  { label: '予約開始', light: '3日前〜', premium: '7日前〜', highlight: false },
  { label: '追加人数', light: '+¥1,000/名（最大4名）', premium: '+¥1,000/名（最大4名）', highlight: false },
];

function CampaignBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap"
      style={{ backgroundColor: '#e85d2c' }}
    >
      <i className="ri-flashlight-fill text-[9px]"></i>
      キャンペーン価格
    </span>
  );
}

function StrikePrice({ price }: { price: string }) {
  return (
    <span className="text-[#1a1a3e]/30 text-[12px] line-through font-normal">
      通常 {price}
    </span>
  );
}

export default function Price() {
  const [extOpen, setExtOpen] = useState(false);
  const [visitorOpen, setVisitorOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<MobileTab>('premium');

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: tableRef, isVisible: tableVisible } = useScrollAnimation();
  const { ref: visitorRef, isVisible: visitorVisible } = useScrollAnimation();
  const { ref: optionsRef, isVisible: optionsVisible } = useScrollAnimation();

  return (
    <section id="price" className="py-24 bg-[#fafafa]">
      <div className="px-6 lg:px-20 max-w-5xl mx-auto">

        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-16 transition-all duration-[600ms] ease-out ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a1a3e] leading-tight mb-4"
            style={{ fontFamily: "'Noto Serif JP', serif", textWrap: 'balance', wordBreak: 'auto-phrase' } as React.CSSProperties}
          >
            あなたのゴルフを、もっと自由に。
          </h2>
          <p className="text-[#1a1a3e]/50 text-[16px]">ライフスタイルに合わせた料金プラン。</p>
          <p className="text-[#1a1a3e]/35 text-[14px] mt-2">営業時間 6:00〜23:55 ／ 料金はすべて税込</p>
        </div>

        {/* ── Desktop: Comparison Table ── */}
        <div
          ref={tableRef}
          className={`hidden md:block overflow-x-auto mb-8 transition-all duration-[600ms] ease-out ${tableVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <table className="w-full min-w-[540px] border-separate border-spacing-x-3">
            <thead>
              <tr>
                <th className="w-1/3 pb-4"></th>
                <th className="w-1/3 pb-4">
                  <div
                    className={`bg-white rounded-2xl border border-[#1a1a3e]/8 px-6 pt-6 pb-5 text-left transition-all duration-[600ms] ease-out delay-[100ms] ${tableVisible ? 'opacity-100 -translate-x-0' : 'opacity-0 -translate-x-8'}`}
                  >
                    <span className="text-[#1a1a3e]/40 text-[12px] font-semibold tracking-[0.18em] uppercase block mb-2">Light</span>
                    <span className="text-[11px] text-[#1a1a3e]/35 font-normal block mb-2">まず試すならこちら</span>
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <StrikePrice price="¥13,200" />
                      <CampaignBadge />
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-[#1a1a3e]" style={{ fontFamily: "'Noto Serif JP', serif" }}>¥9,900</span>
                      <span className="text-[#1a1a3e]/40 text-[13px]">/月</span>
                    </div>
                  </div>
                </th>
                <th className="w-1/3 pb-4">
                  <div
                    className={`bg-white rounded-2xl border-2 border-[#47d6d4] px-6 pt-5 pb-5 text-left transition-all duration-[600ms] ease-out delay-[200ms] ${tableVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
                  >
                    <div className="flex justify-center mb-4">
                      <span className="bg-[#47d6d4] text-white text-[11px] font-bold px-4 py-1 rounded-full whitespace-nowrap">おすすめ</span>
                    </div>
                    <span className="text-[#47d6d4] text-[12px] font-semibold tracking-[0.18em] uppercase block mb-2">Premium</span>
                    <span className="text-[11px] text-[#47d6d4]/60 font-normal block mb-2">一番人気のプラン</span>
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <StrikePrice price="¥19,800" />
                      <CampaignBadge />
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-[#1a1a3e]" style={{ fontFamily: "'Noto Serif JP', serif" }}>¥16,900</span>
                      <span className="text-[#1a1a3e]/40 text-[13px]">/月</span>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, i) => (
                <tr key={i}>
                  <td className="py-3 pr-4 text-[#1a1a3e]/50 text-[14px] font-medium whitespace-nowrap align-middle">
                    {row.label}
                  </td>
                  <td className="py-3">
                    <div className="bg-white border border-[#1a1a3e]/6 rounded-xl px-5 py-3.5 h-full">
                      <span className={`text-[15px] ${row.highlight ? 'font-bold text-[#1a1a3e] text-xl' : 'text-[#1a1a3e]/70'}`}>
                        {row.light}
                      </span>
                    </div>
                  </td>
                  <td className="py-3">
                    <div className="bg-[#47d6d4]/5 border border-[#47d6d4]/25 rounded-xl px-5 py-3.5 h-full">
                      <span className={`text-[15px] ${row.highlight ? 'font-bold text-[#1a1a3e] text-xl' : 'text-[#1a1a3e]/70'}`}>
                        {row.premium}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td></td>
                <td className="pt-5 pb-2">
                  <a
                    href="https://lin.ee/IX2y4CH"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center border-2 border-[#47d6d4] text-[#47d6d4] font-bold px-5 py-3 rounded-full text-[14px] hover:bg-[#47d6d4]/8 transition-all duration-200 whitespace-nowrap w-full"
                  >
                    まずは無料体験から
                  </a>
                </td>
                <td className="pt-5 pb-2">
                  <a
                    href="https://lin.ee/IX2y4CH"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center bg-[#47d6d4] text-white font-bold px-5 py-3 rounded-full text-[14px] hover:bg-[#3bc4c2] transition-all duration-200 whitespace-nowrap w-full"
                  >
                    まずは無料体験から
                  </a>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* ── Mobile: Tab Switcher + Plan Cards ── */}
        <div className="md:hidden mb-8">
          <div className="flex bg-[#f0f0f4] rounded-2xl p-1 mb-6">
            <button
              onClick={() => setActiveTab('light')}
              className={`flex-1 py-2 rounded-xl text-[14px] font-bold transition-all duration-200 cursor-pointer ${
                activeTab === 'light' ? 'bg-white text-[#1a1a3e]' : 'text-[#1a1a3e]/40'
              }`}
            >
              ライト
            </button>
            <button
              onClick={() => setActiveTab('premium')}
              className={`flex-1 py-2 rounded-xl text-[14px] font-bold transition-all duration-200 cursor-pointer flex flex-col items-center justify-center gap-0.5 ${
                activeTab === 'premium' ? 'bg-[#47d6d4] text-white' : 'text-[#1a1a3e]/40'
              }`}
            >
              <span>プレミアム</span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${
                activeTab === 'premium' ? 'bg-white/25 text-white' : 'bg-[#47d6d4]/15 text-[#47d6d4]'
              }`}>おすすめ</span>
            </button>
          </div>

          {/* Light Card */}
          {activeTab === 'light' && (
            <div className="bg-white rounded-2xl border border-[#1a1a3e]/10 overflow-hidden">
              <div className="px-6 py-5 border-b border-[#1a1a3e]/6">
                <span className="text-[#1a1a3e]/40 text-[11px] font-semibold tracking-[0.18em] uppercase block mb-1">Light</span>
                <span className="text-[12px] text-[#1a1a3e]/35 block mb-2">まず試すならこちら</span>
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <StrikePrice price="¥13,200" />
                  <CampaignBadge />
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-[#1a1a3e]" style={{ fontFamily: "'Noto Serif JP', serif" }}>¥9,900</span>
                  <span className="text-[#1a1a3e]/40 text-[13px]">/月</span>
                </div>
              </div>
              <ul className="px-6 py-4 space-y-3">
                {comparisonRows.slice(1).map((row, i) => (
                  <li key={i} className="flex items-start justify-between gap-3">
                    <span className="text-[#1a1a3e]/45 text-[13px] min-w-[72px] flex-shrink-0">{row.label}</span>
                    <span className="text-[#1a1a3e]/75 text-[14px] font-medium text-right">{row.light}</span>
                  </li>
                ))}
              </ul>
              <div className="px-6 pb-6">
                <a
                  href="https://lin.ee/IX2y4CH"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center border-2 border-[#47d6d4] text-[#47d6d4] font-bold px-5 py-3 rounded-full text-[14px] hover:bg-[#47d6d4]/8 transition-all duration-200 w-full"
                >
                  まずは無料体験から
                </a>
              </div>
            </div>
          )}

          {/* Premium Card */}
          {activeTab === 'premium' && (
            <div className="bg-white rounded-2xl border-2 border-[#47d6d4] overflow-hidden">
              <div className="px-6 py-5 border-b border-[#47d6d4]/15 bg-[#47d6d4]/5">
                <span className="text-[#47d6d4] text-[11px] font-semibold tracking-[0.18em] uppercase block mb-1">Premium</span>
                <span className="text-[12px] text-[#47d6d4]/60 block mb-2">一番人気のプラン</span>
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <StrikePrice price="¥19,800" />
                  <CampaignBadge />
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-[#1a1a3e]" style={{ fontFamily: "'Noto Serif JP', serif" }}>¥16,900</span>
                  <span className="text-[#1a1a3e]/40 text-[13px]">/月</span>
                </div>
              </div>
              <ul className="px-6 py-4 space-y-3">
                {comparisonRows.slice(1).map((row, i) => (
                  <li key={i} className="flex items-start justify-between gap-3">
                    <span className="text-[#1a1a3e]/45 text-[13px] min-w-[72px] flex-shrink-0">{row.label}</span>
                    <span className="text-[#1a1a3e]/75 text-[14px] font-medium text-right">{row.premium}</span>
                  </li>
                ))}
              </ul>
              <div className="px-6 pb-6">
                <a
                  href="https://lin.ee/IX2y4CH"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center bg-[#47d6d4] text-white font-bold px-5 py-3 rounded-full text-[14px] hover:bg-[#3bc4c2] transition-all duration-200 w-full"
                >
                  まずは無料体験から
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Extension pricing accordion */}
        <div className="bg-white rounded-xl border border-[#1a1a3e]/6 px-6 py-4 mb-6">
          <button
            className="flex items-center justify-between w-full text-left cursor-pointer"
            onClick={() => setExtOpen(!extOpen)}
          >
            <span className="text-[#1a1a3e]/55 text-[14px] font-medium">延長料金の詳細</span>
            <i className={`ri-arrow-${extOpen ? 'up' : 'down'}-s-line text-[#47d6d4] text-lg`}></i>
          </button>
          {extOpen && (
            <div className="mt-3 pt-3 border-t border-[#1a1a3e]/6 text-[#1a1a3e]/55 text-[14px] leading-[1.75]">
              <ul className="space-y-1.5">
                <li>+1h ¥1,000/名</li>
                <li>+2h ¥1,800/名</li>
                <li>+3h ¥2,400/名</li>
              </ul>
              <p className="text-[#1a1a3e]/35 text-[13px] mt-2">※ 延長料金は「延長時間 × 利用人数」で計算</p>
            </div>
          )}
        </div>

        {/* Coupon Card — Coming Soon */}
        <div className="relative bg-white rounded-xl border border-dashed border-[#1a1a3e]/15 px-7 py-5 mb-6 overflow-hidden">
          {/* Coming Soon overlay (centered) */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            <span className="inline-flex items-center gap-2 bg-[#47d6d4] text-white text-[12px] sm:text-[13px] font-bold tracking-[0.25em] uppercase px-5 py-2.5 rounded-full shadow-lg">
              <i className="ri-time-line text-[14px]"></i>
              Coming Soon
            </span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 opacity-30">
            <div>
              <p className="text-[#1a1a3e]/40 text-[12px] tracking-widest uppercase font-semibold mb-1">Coupon</p>
              <div className="flex items-baseline gap-2.5 flex-wrap">
                <span className="text-3xl font-bold text-[#1a1a3e]" style={{ fontFamily: "'Noto Serif JP', serif" }}>¥40,000</span>
                <span className="text-[#1a1a3e]/45 text-[15px]">10回 × 55分</span>
              </div>
            </div>
            <span className="inline-flex items-center justify-center border border-[#1a1a3e]/15 text-[#1a1a3e]/40 font-bold px-6 py-2.5 rounded-full text-[14px] whitespace-nowrap flex-shrink-0 cursor-not-allowed">
              販売準備中
            </span>
          </div>
          <div className="border-t border-[#1a1a3e]/6 mt-4 pt-3 opacity-60">
            <p className="text-[#1a1a3e]/50 text-[13px] leading-[1.7]">
              現在、回数券は販売準備中です。販売開始時には公式LINEにてお知らせいたします。
            </p>
          </div>
        </div>

        {/* Option */}
        <div className="bg-[#fafafa] rounded-xl px-6 py-4 border border-[#1a1a3e]/6 mb-6">
          <span className="text-[#47d6d4] text-[12px] font-bold mr-3">オプション</span>
          <span className="text-[#1a1a3e]/50 text-[15px]">
            インストラクター <strong className="text-[#1a1a3e]">¥3,300/h</strong> — 機器の使い方・数値の見方をご案内
          </span>
        </div>

        {/* Notes */}
        <p className="text-[#1a1a3e]/30 text-[13px] leading-[1.7] mb-14">
          ※ 予約消化まで次予約は不可 ／ 1日1回まで ／ 最大4名まで ／ 料金はすべて税込
        </p>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-14">
          <hr className="flex-1 border-[#1a1a3e]/10" />
          <span className="text-[#1a1a3e]/30 text-[13px] whitespace-nowrap px-2">または</span>
          <hr className="flex-1 border-[#1a1a3e]/10" />
        </div>

        {/* ── Visitor Section ── */}
        <div
          ref={visitorRef}
          className={`transition-all duration-[600ms] ease-out ${visitorVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="text-center mb-10">
            <h3
              className="text-2xl sm:text-3xl font-bold text-[#1a1a3e] mb-3"
              style={{ fontFamily: "'Noto Serif JP', serif", textWrap: 'balance', wordBreak: 'auto-phrase' } as React.CSSProperties}
            >
              ビジター利用（都度利用）
            </h3>
            <p className="text-[#1a1a3e]/50 text-[16px]">最大4名まで同一料金。予約は前日〜当日。</p>
          </div>

          <div className="bg-white rounded-2xl border border-[#1a1a3e]/8 hover:border-[#47d6d4]/40 transition-all duration-300 px-6 sm:px-8 py-8">
            <p className="text-[#1a1a3e]/40 text-[12px] tracking-[0.2em] uppercase font-semibold mb-6">Visitor Rate</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-5 mb-6">
              {[
                { h: '1h', p: '¥5,000' },
                { h: '2h', p: '¥9,000' },
                { h: '3h', p: '¥13,000' },
                { h: '4h', p: '¥16,000' },
              ].map((item) => (
                <div key={item.h} className="text-center bg-[#fafafa] rounded-xl py-4 border border-[#1a1a3e]/5">
                  <div className="text-[#1a1a3e]/40 text-[13px] mb-1.5 font-medium">{item.h}</div>
                  <div className="text-xl sm:text-2xl font-bold text-[#1a1a3e]" style={{ fontFamily: "'Noto Serif JP', serif" }}>{item.p}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
              <div className="border-t border-[#1a1a3e]/6 pt-4 flex-1">
                <button
                  className="flex items-center gap-1.5 text-[#1a1a3e]/40 text-[13px] cursor-pointer hover:text-[#47d6d4] transition-colors"
                  onClick={() => setVisitorOpen(!visitorOpen)}
                >
                  <i className={`ri-arrow-${visitorOpen ? 'up' : 'down'}-s-line text-[#47d6d4]`}></i>
                  延長について
                </button>
                {visitorOpen && (
                  <p className="text-[#1a1a3e]/50 text-[14px] leading-[1.75] mt-2">
                    延長 +1h ¥5,000（空き状況により対応）
                  </p>
                )}
              </div>
              <a
                href="https://tdbs.me/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#47d6d4] text-white font-bold px-8 py-3.5 rounded-full text-[15px] hover:bg-[#3bc4c2] transition-all duration-200 whitespace-nowrap flex-shrink-0"
              >
                ビジター予約する
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>

        {/* ── Rental / Equipment Options ── */}
        <div
          ref={optionsRef}
          className={`mt-14 transition-all duration-[600ms] ease-out ${optionsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <h4
            className="text-xl sm:text-2xl font-bold text-[#1a1a3e] text-center mb-8"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            備品・オプション
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              {
                title: 'レンタルクラブ',
                price: '無料',
                priceColor: '#47d6d4',
                desc: '男女を問わず、初心者から中級者に向けた最新のクラブをご利用いただけます。キッズクラブもご用意。',
                img: 'https://static.readdy.ai/image/faab8405af96b700637a376baf26c6c6/6a90924c7050f937e6bb43f7fffe2417.jpeg',
                alt: 'レンタルクラブ一式',
              },
              {
                title: 'レンタルシューズ',
                price: '無料',
                priceColor: '#47d6d4',
                desc: 'シミュレーションゴルフに最適なシューズをご利用いただけます。（22cm〜29cmまで1cm刻み）',
                img: 'https://static.readdy.ai/image/faab8405af96b700637a376baf26c6c6/158fba25a7e3645f8bf5bb4ec38d5704.jpeg',
                alt: 'レンタルシューズ',
              },
              {
                title: 'ボール・グローブ',
                price: '現地決済',
                priceColor: '#e85d2c',
                desc: 'ボールのスペックはスコアに直結します。グリップ力を高めるグリップもしっかり男女サイズでご用意。',
                img: 'https://static.readdy.ai/image/faab8405af96b700637a376baf26c6c6/8a973ef4b4b6cc1df8597c62c5bf91bd.jpeg',
                alt: 'ゴルフボールとグローブ',
              },
              {
                title: 'ドリンク & 軽食',
                price: '現地決済',
                priceColor: '#e85d2c',
                desc: 'ソフトドリンク、ナッツなどの軽食をご用意。プレイのお供にご利用ください。（アルコールはイベント時のみ）',
                img: 'https://static.readdy.ai/image/faab8405af96b700637a376baf26c6c6/ee4dacf48a106969eaad26a3403d889f.jpeg',
                alt: 'ドリンクと軽食',
              },
            ].map((item, i) => (
              <div
                key={item.title}
                className={`bg-white rounded-2xl border border-[#1a1a3e]/8 overflow-hidden hover:border-[#47d6d4]/30 transition-all duration-[600ms] ease-out ${
                  optionsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-full overflow-hidden" style={{ aspectRatio: '16/9' }}>
                  <img src={item.img} alt={item.alt} className="w-full h-full object-cover object-top" />
                </div>
                <div className="px-6 py-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[#1a1a3e] text-[16px] font-bold">{item.title}</span>
                    <span
                      className="text-[13px] font-bold px-3 py-0.5 rounded-full"
                      style={{ color: item.priceColor, backgroundColor: `${item.priceColor}15` }}
                    >
                      {item.price}
                    </span>
                  </div>
                  <p
                    className="text-[#1a1a3e]/55 text-[14px] leading-[1.8]"
                    style={{ wordBreak: 'auto-phrase' } as React.CSSProperties}
                  >
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
